import {
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  Notifications as NotificationsIcon,
  NotificationsOff as NotificationsOffIcon,
  Refresh as RefreshIcon,
  TrendingDown as TrendingDownIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
} from '@mui/icons-material'
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Collapse,
  Fab,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNotify, useStore, useTranslate } from 'react-admin'
import { useNavigate } from 'react-router-dom'
import { type AlertAccess, getAlertAccess } from '../authProvider'
import { formatGlucoseForDisplay } from '../common'
import { deadlineFetch } from '../hooks/customEndpointFetch'
import { getAlertSeverityColor } from '../types/alerts'
import { authHeaders } from '../utils/api'
import { type GlucoseUnit, localizeGlucoseInMessage } from '../utils/glucoseUnits'
import {
  announceAlertAcknowledged,
  beginAcknowledge,
  endAcknowledge,
  isAcknowledgeInFlight,
  isKnownAcknowledged,
  notifyUnlessAcknowledged,
  useAcknowledgementsFromOtherSurfaces,
  withKnownAcks,
} from './alertAckEvents'
import { glucoseDirection } from './alertGlucoseDirection'
import { fetchAlertsSince } from './alertPollWindow'
import { mergeAlertsById } from './mergeAlertsById'

// Get API base URL from environment (same as dataProvider)
const apiUrl = import.meta.env.VITE_BACKEND_API_URL || '/api'

/**
 * Simple Alert Panel - Database Polling Approach
 *
 * Perfect for veterinary glucose monitoring with 5+ minute delays
 * - Polls cgm-admin API every 5 minutes for new alerts
 * - Simple acknowledgment via API calls
 * - No complex real-time subscriptions
 * - Works with existing nginx proxy configuration
 */

interface SimpleAlert {
  id: number
  petId: number
  alertType: string
  severity: 'critical' | 'high' | 'medium' | 'low'
  title: string
  message: string
  triggeredAt: string
  acknowledgedAt?: string
  viewedAt?: string
  suppressedUntil?: string
  glucoseValue?: number
  recommendations?: string[]
  notes?: string
}

interface SimpleAlertPanelProps {
  petId?: number
  compact?: boolean
  pollingInterval?: number // in milliseconds, default 5 minutes
}

const SimpleAlertPanel: React.FC<SimpleAlertPanelProps> = ({
  petId,
  compact = false,
  pollingInterval = 5 * 60 * 1000, // 5 minutes default
}) => {
  const translate = useTranslate()
  const notify = useNotify()
  const navigate = useNavigate()
  const [glucoseUnit] = useStore<GlucoseUnit>('glucoseUnit', 'mmol/L')

  const [alerts, setAlerts] = useState<SimpleAlert[]>([])
  // Mirror of `alerts` so the async poll handler can merge an incremental
  // batch into the CURRENT list without a stale closure (L-020). Synced by
  // the effect below; read only inside fetchAlerts.
  const alertsRef = useRef<SimpleAlert[]>([])
  const [totalUnacknowledged, setTotalUnacknowledged] = useState(0) // Server-side count
  const [loading, setLoading] = useState(false)
  const [expandedAlert, setExpandedAlert] = useState<number | null>(null)
  // When false (default), resolved (acknowledged) alerts are hidden from the
  // active list so a handled alert "disappears"; the toggle reveals them for review.
  const [showResolved, setShowResolved] = useState(false)
  // Optional resolution note the vet types in the expanded alert's action bar.
  const [resolveNotes, setResolveNotes] = useState('')
  const [newAlertsCount, setNewAlertsCount] = useState(0)
  // null = still checking. 'unknown' is NOT 'denied' — see getAlertAccess.
  const [alertAccess, setAlertAccess] = useState<AlertAccess | null>(null)
  const hasAlertPermission = alertAccess === 'allowed'
  const [accessRetry, setAccessRetry] = useState(0)
  const [refreshTrigger, setRefreshTrigger] = useState(0) // Increment to trigger manual refresh
  const [fetchError, setFetchError] = useState<string | null>(null) // Track fetch errors for display

  // Use ref for lastCheck to avoid useEffect dependency loops
  // lastCheck is updated after each fetch and shouldn't trigger new fetches
  const lastCheckRef = useRef<Date>(new Date(Date.now() - 24 * 60 * 60 * 1000))

  // Track the previous petId to detect changes and reset lastCheck
  const prevPetIdRef = useRef<number | undefined>(petId)

  // Check alert access. Re-runs when the user asks to retry, because an
  // 'unknown' answer means the identity call failed, not that the answer is no.
  useEffect(() => {
    getAlertAccess().then((access) => {
      setAlertAccess(access)
      if (access !== 'allowed') {
        console.log(
          `SimpleAlertPanel: alert access = ${access} (allowed requires admin or vet role)`,
        )
      }
    })
  }, [accessRetry])

  // Keep the merge mirror in sync with the displayed list (runs after every
  // commit, so acknowledge/optimistic updates are reflected before the next poll).
  useEffect(() => {
    alertsRef.current = alerts
  }, [alerts])

  // #42 (R39): another surface of this tab acknowledged an alert. The list takes
  // it (and every later merge re-applies it); the all-pets total is RE-POLLED --
  // subtracting could double-count an ack a newer poll already reflects, and a
  // count too low hides a real alert.
  useAcknowledgementsFromOtherSurfaces(setAlerts, () => {
    if (!petId) setRefreshTrigger((t) => t + 1)
  })

  // Reset lastCheck when petId changes to get fresh data for the new pet context
  useEffect(() => {
    if (prevPetIdRef.current !== petId) {
      // Reset to 24 hours ago to get all recent alerts for the new pet context
      lastCheckRef.current = new Date(Date.now() - 24 * 60 * 60 * 1000)
      prevPetIdRef.current = petId
      // NEVER show the previous patient's alerts on the new patient's page
      // (L-020/L-007): clear immediately — the polling effect re-runs on the
      // same petId change and repopulates from the new pet's 24h window. The
      // old code only reset lastCheck, so when the new pet had NO alerts the
      // prior pet's (possibly critical) alerts stayed rendered indefinitely.
      setAlerts([])
      setTotalUnacknowledged(0)
      setNewAlertsCount(0)
    }
  }, [petId])

  // Simple polling for alerts
  useEffect(() => {
    // Skip polling if user doesn't have alert permissions
    if (!hasAlertPermission) {
      return
    }

    // One effect run = one pet / refresh generation. The cleanup below ends the
    // run, and a response that resolves after that is DROPPED: its closure still
    // filters by the previous petId, and merging it would put the previous
    // patient's alerts on this patient's list (VP-ALERTS-R07, AlertPoll.tla F1).
    let cancelled = false
    const controller = new AbortController()

    const fetchAlerts = async () => {
      try {
        setLoading(true)
        setFetchError(null) // Clear previous errors on new fetch

        // Auth via cookie OR token (L-019): on a browser reopen / new tab
        // sessionStorage is empty but the HttpOnly payload-token cookie is still
        // valid, so fire the same-origin request and let the cookie carry auth.
        // Bailing on a missing token used to silently blank the alert panel on
        // every reopen — a vet would not see hypo/critical alerts until a fresh
        // login. authHeaders() omits the header when there is no token.
        const walk = await fetchAlertsSince(apiUrl, lastCheckRef.current, {
          headers: authHeaders(),
          credentials: 'same-origin',
          signal: controller.signal,
        })
        if (cancelled) return

        // Handle non-OK responses explicitly instead of silently ignoring
        if (!walk.ok) {
          const { response } = walk
          const errorMessage =
            response.status === 401 || response.status === 403
              ? 'Authentication error - please refresh the page'
              : response.status >= 500
                ? 'Alert service temporarily unavailable'
                : `Failed to fetch alerts (${response.status})`
          setFetchError(errorMessage)
          console.error('Alert fetch failed:', response.status, response.statusText)
          return
        }

        // The walk in the legacy body shape. An INCOMPLETE walk (page cap / no
        // progress) carries no lastUpdate, so the watermark below holds and the
        // next poll re-reads (ada-core#230, AlertPoll.tla F4).
        const data = {
          success: true,
          data: {
            alerts: walk.alerts as any[],
            summary: walk.summary,
            lastUpdate: walk.complete ? walk.lastUpdate : undefined,
          },
        }

        if (data.success) {
          setFetchError(null) // Clear error on success
          const newAlerts = data.data.alerts.map((alert: any) => {
            // Extract pet_id from both camelCase and snake_case
            const petId = alert.petId || alert.pet_id || null

            // Log warning if alert is missing pet_id
            if (!petId) {
              console.warn('SimpleAlertPanel: Alert missing pet_id:', {
                alertId: alert.id,
                deviceId: alert.device_id || alert.deviceId,
                title: alert.title,
              })
            }

            return {
              id: alert.id,
              petId: petId,
              alertType: alert.alertType || alert.alert_type,
              severity: (alert.severity || 'medium').toLowerCase(),
              title: alert.title,
              message: alert.message,
              triggeredAt: alert.triggeredAt || alert.triggered_at,
              acknowledgedAt: alert.acknowledgedAt || alert.acknowledged_at,
              viewedAt: alert.viewedAt || alert.viewed_at,
              suppressedUntil: alert.suppressedUntil || alert.suppressed_until,
              glucoseValue: alert.glucoseValue || alert.glucose_value,
              recommendations: alert.recommendations || [],
              notes: alert.notes || alert.actionTaken || alert.action_taken,
            }
          })

          // Filter by petId if specified (strict matching)
          const filteredAlerts = petId
            ? newAlerts.filter((alert: SimpleAlert) => {
                const alertPetId = alert.petId
                const petIdNum = typeof petId === 'string' ? parseInt(petId, 10) : petId
                const alertPetIdNum =
                  typeof alertPetId === 'string' ? parseInt(alertPetId, 10) : alertPetId
                return alertPetIdNum === petIdNum
              })
            : newAlerts

          // The endpoint is INCREMENTAL (alerts since lastCheck) — MERGE the
          // batch into the displayed list, never assign it (L-020: replacing
          // dropped previously displayed unacknowledged criticals the moment
          // any newer alert arrived). New batch wins on id collision so a
          // re-fetched alert refreshes its acknowledgedAt.
          // The poll re-reads an overlap margin (F3), so "new" means an id the
          // list does not already hold — never re-count or re-notify those.
          const listedIds = new Set(alertsRef.current.map((alert) => alert.id))
          const freshAlerts = filteredAlerts.filter(
            (alert: SimpleAlert) => !listedIds.has(alert.id),
          )
          // R39: re-apply this tab's recorded acknowledgements -- this batch may
          // predate one another surface just made.
          // FUNCTIONAL, never from alertsRef: the ref syncs in a passive effect
          // after the commit, so a response landing in between merged from the
          // list BEFORE a failed acknowledge's revert and put the refused alert
          // back to resolved for the session (AckSurfaces.tla K2).
          setAlerts((prev) => withKnownAcks(mergeAlertsById(prev, filteredAlerts, 100)))

          // No pet: the badge is the server's total. With a pet it is derived
          // from the list (shownUnacknowledged). A walk that hit the page cap
          // carries NO summary: keep the last total rather than show 0 -- a
          // count too low hides real alerts. With NO total yet (the first poll)
          // "the last total" is the initial 0, so the unacknowledged alerts this
          // walk actually loaded are a FLOOR: raise to them, never lower (R46).
          const serverTotal = data.data.summary?.unacknowledged
          if (!petId) {
            if (serverTotal != null) {
              setTotalUnacknowledged(serverTotal)
            } else {
              const loaded = withKnownAcks(filteredAlerts).filter((a) => !a.acknowledgedAt).length
              setTotalUnacknowledged((prev) => Math.max(prev, loaded))
            }
          }

          // Advance the watermark on a non-empty RAW batch (P63-6, unified
          // with AlertMenuButton): the alerts up to lastUpdate are in hand, so
          // it is safe to advance even when THIS pet's filter empties the
          // batch — the merge dedups by id, and gating on the filtered batch
          // just refetched other pets' alerts forever while the two sibling
          // surfaces diverged in their recovery windows.
          if (newAlerts.length > 0 && data.data.lastUpdate) {
            lastCheckRef.current = new Date(data.data.lastUpdate)
          }

          if (freshAlerts.length > 0) {
            setNewAlertsCount(freshAlerts.length)

            // Show browser notification for critical alerts. UNACKNOWLEDGED
            // only: the pet-switch reset rewinds lastCheck 24h, so this batch
            // can contain criticals the vet already handled — re-popping those
            // is alert fatigue, and the alert still renders in the list.
            freshAlerts.forEach((alert: SimpleAlert) => {
              if (
                alert.severity === 'critical' &&
                !alert.acknowledgedAt &&
                // Read before an acknowledge this tab has since landed (the
                // bell's click-through): no desktop notification (K1).
                !isKnownAcknowledged(alert.id) &&
                'Notification' in window &&
                Notification.permission === 'granted'
              ) {
                const show = () =>
                  new Notification(alert.title, {
                    body: localizeGlucoseInMessage(alert.message, glucoseUnit),
                    icon: '/favicon.ico',
                  })
                // An acknowledge of it is still in flight (the bell's click-
                // through): notify only if that acknowledge fails (R45, K1b).
                if (isAcknowledgeInFlight(alert.id)) notifyUnlessAcknowledged(alert.id, show)
                else show()
              }
            })
          }
        }
      } catch (error) {
        if (cancelled) return // our own abort on cleanup, not a feed failure
        const errorMessage =
          error instanceof Error
            ? `Network error: ${error.message}`
            : 'Unable to connect to alert service'
        setFetchError(errorMessage)
        console.error('Error fetching alerts:', error)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    // Initial fetch
    fetchAlerts()

    // Set up polling interval
    const interval = setInterval(fetchAlerts, pollingInterval)

    // Request notification permission on first load
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission()
    }

    return () => {
      cancelled = true
      controller.abort()
      clearInterval(interval)
    }
  }, [petId, pollingInterval, hasAlertPermission, refreshTrigger]) // refreshTrigger: manual refresh, and R39's re-poll

  const getSeverityIcon = (alert: SimpleAlert) => {
    const color = getAlertSeverityColor(alert.severity)
    const iconProps = { style: { color } }

    switch (glucoseDirection(alert.alertType)) {
      case 'down':
        return <TrendingDownIcon {...iconProps} />
      case 'up':
        return <TrendingUpIcon {...iconProps} />
      default:
        return alert.severity === 'critical' ? (
          <ErrorIcon {...iconProps} />
        ) : alert.severity === 'high' ? (
          <WarningIcon {...iconProps} />
        ) : (
          <InfoIcon {...iconProps} />
        )
    }
  }

  const getAlertTypeLabel = (type: string): string => {
    const key = `pos.alerts.type.${type}`
    const translated = translate(key)
    return translated !== key ? translated : type
  }

  const handleAcknowledge = useCallback(
    async (alertId: number, silent = false, notes?: string) => {
      // Outside the try, so the finally's endAcknowledge always has its pair (R45).
      beginAcknowledge(alertId)
      try {
        const acknowledgedAt = new Date().toISOString()
        // Optimistically update UI first
        setAlerts((prev) =>
          prev.map((alert) => (alert.id === alertId ? { ...alert, acknowledgedAt } : alert)),
        )
        // Decrement the total unacknowledged count optimistically
        setTotalUnacknowledged((prev) => Math.max(0, prev - 1))

        // Cookie-or-token auth (L-019) — an acknowledge on a cookie-only reopen
        // must not silently no-op.
        const response = await deadlineFetch(`${apiUrl}/alert-events/acknowledge-alert`, {
          method: 'POST',
          headers: authHeaders(),
          credentials: 'same-origin',
          body: JSON.stringify({
            alertId,
            // Vet-entered resolution note when provided; otherwise a default marker.
            notes:
              notes?.trim() ||
              (silent
                ? 'Auto-acknowledged on view from vetpo dashboard'
                : 'Resolved from vetpo dashboard'),
          }),
        })

        if (response.ok) {
          announceAlertAcknowledged(alertId, acknowledgedAt) // #42: tell the bell
          if (!silent) {
            notify(translate('pos.alerts.acknowledge_success'), { type: 'success' })
          }
        } else {
          // Revert optimistic update on failure
          // Unless another surface's acknowledge of it already SUCCEEDED (R39):
          // the server holds it acknowledged, so do not show it unresolved again.
          if (!isKnownAcknowledged(alertId)) {
            setAlerts((prev) =>
              prev.map((alert) =>
                alert.id === alertId ? { ...alert, acknowledgedAt: undefined } : alert,
              ),
            )
            // No pet: RE-POLL the server's total -- a poll that landed since the
            // optimistic -1 already counts it, so +1 counted it twice (K3).
            if (!petId) setRefreshTrigger((t) => t + 1)
          }
          if (!silent) {
            notify(translate('pos.alerts.acknowledge_failed'), { type: 'error' })
          }
        }
      } catch (error) {
        console.error('Error acknowledging alert:', error)
        // Revert optimistic update on error
        // Unless another surface's acknowledge of it already SUCCEEDED (R39):
        // the server holds it acknowledged, so do not show it unresolved again.
        if (!isKnownAcknowledged(alertId)) {
          setAlerts((prev) =>
            prev.map((alert) =>
              alert.id === alertId ? { ...alert, acknowledgedAt: undefined } : alert,
            ),
          )
          // No pet: RE-POLL the server's total -- a poll that landed since the
          // optimistic -1 already counts it, so +1 counted it twice (K3).
          if (!petId) setRefreshTrigger((t) => t + 1)
        }
        if (!silent) {
          notify(translate('pos.alerts.acknowledge_failed'), { type: 'error' })
        }
      } finally {
        endAcknowledge(alertId) // after any announce: a success is already recorded
      }
    },
    [notify, translate, petId],
  )

  const handleAlertExpand = useCallback(
    async (alertId: number) => {
      const isExpanding = expandedAlert !== alertId

      // Update UI immediately
      setExpandedAlert(isExpanding ? alertId : null)
      // Start each alert's resolution note fresh.
      setResolveNotes('')

      // When expanding an alert, mark it as viewed to trigger cooldown
      // This is part of sustained threshold alerting - viewing an alert
      // starts the cooldown period (1h for critical, 2h for warning)
      if (isExpanding) {
        const alert = alerts.find((a) => a.id === alertId)
        if (alert) {
          // Mark alert as viewed (triggers cooldown for sustained threshold alerting)
          try {
            // Cookie-or-token auth (L-019): fire regardless of a sessionStorage
            // token so "mark as viewed" (sustained-threshold cooldown) still
            // works on a cookie-only reopen.
            await deadlineFetch(`${apiUrl}/alert-events/view`, {
              method: 'POST',
              headers: authHeaders(),
              credentials: 'same-origin',
              body: JSON.stringify({ alertId }),
            })
          } catch (error) {
            console.error('Error marking alert as viewed:', error)
          }
          // NOTE: do NOT auto-acknowledge on expand. Resolving is an explicit
          // action ("Mark as Resolved"); auto-acknowledging on view would mark
          // the alert resolved and immediately remove it from the active list
          // before the vet can act on it.
        }
      }
    },
    [expandedAlert, alerts],
  )

  const handleAlertClick = (alert: SimpleAlert) => {
    // Validate petId before navigation to prevent logout issue
    if (!alert.petId || alert.petId === null || alert.petId === undefined) {
      notify(translate('pos.alerts.navigate_cannot'), { type: 'warning' })
      console.warn('SimpleAlertPanel: Invalid petId for alert:', alert)
      return
    }

    // Navigate to the pet's show page with alert context
    const alertTimestamp = new Date(alert.triggeredAt).getTime()
    const glucoseValue = alert.glucoseValue

    // Build navigation URL with alert context parameters
    const baseUrl = `/pets/${alert.petId}/show`
    const urlParams = new URLSearchParams()

    // Add alert context to URL for chart highlighting
    urlParams.set('alertId', alert.id.toString())
    urlParams.set('alertTimestamp', alertTimestamp.toString())
    urlParams.set('alertType', alert.alertType)

    if (glucoseValue) {
      urlParams.set('alertGlucose', glucoseValue.toString())
    }

    // Smart navigation based on alert type with enhanced context
    if (glucoseDirection(alert.alertType) !== null) {
      // Navigate with glucose context for chart highlighting
      urlParams.set('view', 'glucose')
      urlParams.set('highlight', 'true')
      navigate(`${baseUrl}?${urlParams.toString()}`)

      const glucoseText = glucoseValue
        ? ` (${formatGlucoseForDisplay(glucoseValue, glucoseUnit)})`
        : ''
      notify('pos.alerts.navigating_chart', {
        type: 'info',
        messageArgs: {
          time: new Date(alert.triggeredAt).toLocaleTimeString(),
          glucose: glucoseText,
        },
      })
    } else if (alert.alertType === 'pattern_detected' || alert.alertType === 'somogyi') {
      // Navigate with pattern analysis context
      urlParams.set('view', 'patterns')
      urlParams.set('timeRange', '24h') // Show 24h context for patterns
      navigate(`${baseUrl}?${urlParams.toString()}`)
      notify('pos.alerts.navigating_patterns', {
        type: 'info',
        messageArgs: { date: new Date(alert.triggeredAt).toLocaleDateString() },
      })
    } else {
      // Default navigation with alert context
      urlParams.set('view', 'overview')
      navigate(`${baseUrl}?${urlParams.toString()}`)
      notify('pos.alerts.navigating_overview', {
        type: 'info',
        messageArgs: { title: alert.title || translate('pos.alerts.fallback_title') },
      })
    }

    // Store alert context in sessionStorage for chart components to use
    const alertContext = {
      alertId: alert.id,
      timestamp: alertTimestamp,
      glucoseValue: glucoseValue,
      alertType: alert.alertType,
      severity: alert.severity,
      title: alert.title,
    }

    sessionStorage.setItem('currentAlertContext', JSON.stringify(alertContext))
  }

  const handleRefresh = () => {
    // Reset lastCheck to 24h ago to fetch all recent alerts
    lastCheckRef.current = new Date(Date.now() - 24 * 60 * 60 * 1000)
    setNewAlertsCount(0)
    // Increment trigger to re-run the useEffect and fetch fresh data
    setRefreshTrigger((prev) => prev + 1)
  }

  const unacknowledgedAlerts = alerts.filter((alert) => !alert.acknowledgedAt)
  // What the badge shows (R39): scoped to a pet, the unacknowledged alerts in
  // the LIST -- derived, so no ±1 bookkeeping can drift from what is listed;
  // otherwise the server's total.
  const shownUnacknowledged = petId ? unacknowledgedAlerts.length : totalUnacknowledged
  const criticalAlerts = unacknowledgedAlerts.filter((alert) => alert.severity === 'critical')
  const resolvedAlerts = alerts.filter((alert) => alert.acknowledgedAt)
  // Resolved alerts drop out of the active list unless the vet opts to review them.
  const displayedAlerts = showResolved ? alerts : unacknowledgedAlerts

  if (compact) {
    return (
      <Badge badgeContent={shownUnacknowledged} color="error" max={99}>
        <Fab
          size="small"
          color={
            criticalAlerts.length > 0 ? 'error' : shownUnacknowledged > 0 ? 'warning' : 'default'
          }
          onClick={() => setNewAlertsCount(0)}
          sx={{ position: 'fixed', bottom: 80, right: 16, zIndex: 1000 }}
        >
          {criticalAlerts.length > 0 ? <ErrorIcon /> : <NotificationsIcon />}
        </Fab>
      </Badge>
    )
  }

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <NotificationsIcon />
            <Typography variant="h6">{translate('pos.alerts.title')}</Typography>
            <Chip
              label={translate('pos.alerts.unacknowledged_count', { count: shownUnacknowledged })}
              size="small"
              color={shownUnacknowledged > 0 ? 'error' : 'default'}
            />
          </Box>
        }
        action={
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {resolvedAlerts.length > 0 && (
              <Tooltip
                title={translate(
                  showResolved ? 'pos.alerts.hide_resolved' : 'pos.alerts.show_resolved',
                )}
              >
                <Chip
                  label={translate('pos.alerts.resolved_count', { count: resolvedAlerts.length })}
                  size="small"
                  variant={showResolved ? 'filled' : 'outlined'}
                  color={showResolved ? 'success' : 'default'}
                  onClick={() => setShowResolved((prev) => !prev)}
                />
              </Tooltip>
            )}
            <Tooltip title={translate('pos.alerts.refresh_tooltip')}>
              <IconButton onClick={handleRefresh} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        }
      />

      {loading && <LinearProgress />}

      <CardContent sx={{ flex: 1, overflow: 'auto', py: 0 }}>
        {criticalAlerts.length > 0 && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <IconButton
                color="inherit"
                size="small"
                onClick={() => criticalAlerts.forEach((alert) => handleAcknowledge(alert.id))}
              >
                <CheckIcon />
              </IconButton>
            }
          >
            {translate('pos.alerts.critical_require_attention', { count: criticalAlerts.length })}
          </Alert>
        )}

        {/* Error state - Alert monitoring may not be working */}
        {fetchError && (
          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setFetchError(null)}>
            <Typography variant="body2" fontWeight="bold">
              {translate('pos.alerts.monitoring_issue')}
            </Typography>
            <Typography variant="body2">{fetchError}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {translate('pos.alerts.monitoring_issue_retry')}
            </Typography>
          </Alert>
        )}

        {alertAccess === 'unknown' ? (
          // We could not READ the permission answer (identity call 5xx'd or the
          // network failed). Saying "not available for your role" here would be
          // an authoritative claim about the vet's account that we never
          // actually obtained — the P32-3 defect. Say what is true, and offer
          // the retry the one-shot check otherwise never does.
          <Box sx={{ textAlign: 'center', py: 2 }} data-testid="alerts-access-unknown">
            <NotificationsOffIcon sx={{ fontSize: 28, color: 'text.disabled', mb: 0.5 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {translate('pos.alerts.access_unknown')}
            </Typography>
            <Button size="small" onClick={() => setAccessRetry((n) => n + 1)}>
              {translate('pos.alerts.access_retry')}
            </Button>
          </Box>
        ) : alertAccess === 'denied' ? (
          // This role cannot READ alerts (canAccessAlerts allows only admin/vet
          // while seven roles can sign in), so the poll never runs. Saying "no
          // recent alerts / checking every N minutes" here is an all-clear the
          // app cannot back up — on a diabetic patient that is the worst
          // possible wrong answer. Say what is actually true. (eng-brain P24-2)
          <Box sx={{ textAlign: 'center', py: 2 }} data-testid="alerts-not-permitted">
            <NotificationsOffIcon sx={{ fontSize: 28, color: 'text.disabled', mb: 0.5 }} />
            <Typography variant="body2" color="text.secondary">
              {translate('pos.alerts.not_available_for_role')}
            </Typography>
          </Box>
        ) : displayedAlerts.length === 0 && !fetchError ? (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <NotificationsOffIcon sx={{ fontSize: 28, color: 'text.disabled', mb: 0.5 }} />
            <Typography variant="body2" color="text.secondary">
              {translate(
                resolvedAlerts.length > 0
                  ? 'pos.alerts.all_resolved'
                  : 'pos.alerts.no_recent_alerts',
              )}
            </Typography>
            <Typography variant="caption" color="text.disabled" sx={{ mt: 0.25, display: 'block' }}>
              {translate('pos.alerts.checking_interval', {
                minutes: Math.round(pollingInterval / 60000),
              })}
            </Typography>
          </Box>
        ) : displayedAlerts.length === 0 && fetchError ? null : (
          <List sx={{ width: '100%' }}>
            {displayedAlerts.slice(0, 50).map(
              (
                alert, // Show max 50 alerts
              ) => (
                <React.Fragment key={alert.id}>
                  <ListItem
                    component="button"
                    onClick={() => handleAlertClick(alert)}
                    sx={{
                      borderLeft: 4,
                      borderColor: getAlertSeverityColor(alert.severity),
                      mb: 1,
                      borderRadius: 1,
                      bgcolor: !alert.acknowledgedAt ? 'action.hover' : 'background.paper',
                      opacity: alert.acknowledgedAt ? 0.7 : 1,
                      cursor: 'pointer',
                    }}
                    secondaryAction={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {/* The check ALWAYS means "resolved" (a status), never an
                            action — so it only shows on already-resolved alerts.
                            Resolving is done via the labeled "Mark as Resolved"
                            button in the expanded detail. */}
                        {alert.acknowledgedAt && (
                          <Tooltip title={translate('pos.alerts.resolved')}>
                            <CheckIcon color="success" fontSize="small" />
                          </Tooltip>
                        )}
                        <Tooltip title={translate('pos.alerts.show_details')}>
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleAlertExpand(alert.id)
                            }}
                          >
                            {expandedAlert === alert.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    }
                  >
                    <ListItemIcon>{getSeverityIcon(alert)}</ListItemIcon>

                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle2">{alert.title}</Typography>
                          <Chip
                            label={getAlertTypeLabel(alert.alertType)}
                            size="small"
                            variant="outlined"
                          />
                          {alert.glucoseValue && (
                            <Chip
                              label={formatGlucoseForDisplay(alert.glucoseValue, glucoseUnit)}
                              size="small"
                              color="primary"
                            />
                          )}
                          {alert.acknowledgedAt && (
                            <Chip
                              label={translate('pos.alerts.acknowledged')}
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {new Date(alert.triggeredAt).toLocaleString()}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {localizeGlucoseInMessage(alert.message, glucoseUnit)}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>

                  <Collapse in={expandedAlert === alert.id} timeout="auto" unmountOnExit>
                    <Box sx={{ pl: 4, pr: 2, pb: 2 }}>
                      {alert.recommendations && alert.recommendations.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            {translate('pos.alerts.recommendations')}
                          </Typography>
                          <List dense>
                            {alert.recommendations.map((rec, index) => (
                              <ListItem key={index} sx={{ py: 0.5 }}>
                                <ListItemText
                                  primary={rec}
                                  primaryTypographyProps={{ variant: 'body2' }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      )}

                      {/* Action bar: resolve a handled alert (it then leaves the
                          active list), or show when/that it was already resolved. */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1,
                          mt: 1,
                          pt: 1,
                          borderTop: 1,
                          borderColor: 'divider',
                        }}
                      >
                        {alert.acknowledgedAt ? (
                          <Box>
                            <Typography variant="body2" color="success.main">
                              {translate('pos.alerts.resolved_at', {
                                time: new Date(alert.acknowledgedAt).toLocaleString(),
                              })}
                            </Typography>
                            {alert.notes && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {translate('pos.alerts.resolution_note', { note: alert.notes })}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Box sx={{ width: '100%' }}>
                            <Typography variant="caption" color="text.secondary">
                              {translate('pos.alerts.resolve_hint')}
                            </Typography>
                            <TextField
                              fullWidth
                              multiline
                              minRows={2}
                              size="small"
                              placeholder={translate('pos.alerts.resolve_notes_placeholder')}
                              value={expandedAlert === alert.id ? resolveNotes : ''}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => setResolveNotes(e.target.value)}
                              sx={{ my: 1 }}
                            />
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                startIcon={<CheckIcon />}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleAcknowledge(alert.id, false, resolveNotes)
                                  setResolveNotes('')
                                }}
                              >
                                {translate('pos.alerts.mark_resolved')}
                              </Button>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Collapse>
                </React.Fragment>
              ),
            )}
          </List>
        )}
      </CardContent>
    </Card>
  )
}

export default SimpleAlertPanel
